import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Input,
  CustomInput,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import axios from 'axios';
import Am4chartMapDesil from '../../components/am4chartMap/am4chartMapDesil';
import Am4chartMap from '../../components/am4chartMap/am4chartMap';
import { API_TIMEOUT, BANTUAN_LIST_TAHUN } from '../../utils';
import {
  loadMapSettings,
  saveMapSettings,
  resetMapSettings,
  DEFAULT_MAP_SETTINGS,
  MAP_SETTINGS_CHANGED_EVENT,
} from '../../utils/mapSettings';
import {
  parseMapCsvText,
  saveMapCsvRows,
  loadMapCsvRows,
  clearMapCsvData,
} from '../../utils/mapCsvData';
import { ensureTierPalette } from '../../utils/mapTierUtils';
import { listKecamatanFromGeoFeatures } from '../../utils/mapGeoMerge';
import roteNdaoGeo from '../../assets/jsonData/kab_Rote.json';
import s from './MapCustomizationPage.module.scss';

const CSV_TEMPLATE = 'desa,nilai,kecamatan\nOeseli,120,Rote Barat Daya\nLalukoen,85,Rote Barat Daya\n';

const ColorRow = ({ fields, settings, onColorChange }) => (
  <Row>
    {fields.map(({ key, label }) => (
      <Col md={12} key={key} className="mb-2">
        <FormGroup className="mb-0">
          <Label className="d-flex align-items-center justify-content-between mb-0">
            <span>{label}</span>
            <Input
              type="color"
              value={settings.colors[key] || DEFAULT_MAP_SETTINGS.colors[key]}
              onChange={(e) => onColorChange(key, e.target.value)}
              className="ml-2"
              style={{ width: 48, height: 32, padding: 2, cursor: 'pointer' }}
            />
          </Label>
        </FormGroup>
      </Col>
    ))}
  </Row>
);

const edgeFields = [
  { key: 'stroke', label: 'Garis batas wilayah' },
  { key: 'hover', label: 'Saat sorot wilayah' },
];

const labelColorFields = [{ key: 'kecamatanLabel', label: 'Teks nama kecamatan' }];

/** Dialog pengaturan: data / tampilan / gradasi / warna dasar */
const MapCustomizationPage = () => {
  const [settings, setSettings] = useState(() => loadMapSettings());
  const [settingsPanel, setSettingsPanel] = useState(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [csvError, setCsvError] = useState('');
  const [manualEdit, setManualEdit] = useState(null);
  const [pickerColor, setPickerColor] = useState('#1976d2');
  const [csvRowCount, setCsvRowCount] = useState(() => loadMapCsvRows().length);
  const scrollBeforeColorModalRef = useRef(0);
  const kecamatanMasterRef = useRef(null);
  const desilMapRef = useRef(null);
  const bantuanMapRef = useRef(null);

  const [mapView, setMapView] = useState('desil');
  const currentYear = new Date().getFullYear();
  const [bantuanTahun, setBantuanTahun] = useState(String(currentYear));
  const [bantuanJenis, setBantuanJenis] = useState('all');
  const [bantuanOptions, setBantuanOptions] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const tahunChoices = useMemo(() => {
    const out = [];
    for (let y = 2018; y <= currentYear + 1; y++) out.push(String(y));
    return out;
  }, [currentYear]);

  useEffect(() => {
    if (mapView !== 'bantuan') return;
    axios
      .get(BANTUAN_LIST_TAHUN + bantuanTahun, { timeout: API_TIMEOUT, credentials: 'include' })
      .then((res) => {
        const opts = Array.isArray(res.data?.data) ? res.data.data : [];
        setBantuanOptions(opts);
        setBantuanJenis('all');
      })
      .catch(() => {
        setBantuanOptions([]);
        setBantuanJenis('all');
      });
  }, [mapView, bantuanTahun]);

  const kecamatanList = useMemo(
    () => listKecamatanFromGeoFeatures(roteNdaoGeo.features || []),
    []
  );

  const kecamatanMasterIndeterminate =
    Array.isArray(settings.kecamatanIncludedKeys) &&
    settings.kecamatanIncludedKeys.length > 0 &&
    settings.kecamatanIncludedKeys.length < kecamatanList.length;

  useEffect(() => {
    const el = kecamatanMasterRef.current;
    if (el) el.indeterminate = kecamatanMasterIndeterminate;
  }, [kecamatanMasterIndeterminate]);

  useEffect(() => {
    const sync = () => {
      setSettings(loadMapSettings());
      setCsvRowCount(loadMapCsvRows().length);
    };
    window.addEventListener(MAP_SETTINGS_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(MAP_SETTINGS_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const closePanel = useCallback(() => setSettingsPanel(null), []);

  const setColor = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      colors: { ...prev.colors, [key]: value },
    }));
  };

  const handleSave = () => {
    saveMapSettings(settings);
    setSavedNotice(true);
  };

  const handleReset = () => {
    if (!window.confirm('Kembalikan semua pengaturan peta ke bawaan aplikasi?')) return;
    resetMapSettings();
    setSettings(loadMapSettings());
    setSavedNotice(false);
    setCsvError('');
  };

  const onTierCountChange = (nRaw) => {
    const n = Math.max(2, Math.min(8, Number(nRaw) || 4));
    setSettings((prev) => {
      const palette = ensureTierPalette(prev.tierPalette, n, DEFAULT_MAP_SETTINGS.tierPalette);
      let breaks = [...(prev.manualTierBreaks || [])];
      while (breaks.length < n - 1) breaks.push(breaks.length ? breaks[breaks.length - 1] + 1 : 1);
      if (breaks.length > n - 1) breaks.length = n - 1;
      return { ...prev, tierCount: n, tierPalette: palette, manualTierBreaks: breaks };
    });
  };

  const onBreakChange = (idx, val) => {
    const num = parseFloat(String(val).replace(',', '.'));
    setSettings((prev) => {
      const tc = prev.tierCount || 4;
      const arr = [...(prev.manualTierBreaks || [])];
      while (arr.length < tc - 1) arr.push(0);
      arr[idx] = Number.isFinite(num) ? num : 0;
      for (let i = 1; i < arr.length; i++) {
        if (arr[i] <= arr[i - 1]) arr[i] = arr[i - 1] + 1e-6;
      }
      return { ...prev, manualTierBreaks: arr };
    });
  };

  const onPaletteChange = (idx, hex) => {
    setSettings((prev) => {
      const pal = [...(prev.tierPalette || [])];
      pal[idx] = hex;
      return { ...prev, tierPalette: pal };
    });
  };

  const onCsvFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setCsvError('');
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const rows = parseMapCsvText(String(reader.result || ''));
        saveMapCsvRows(rows);
        setCsvRowCount(rows.length);
      } catch (err) {
        setCsvError(err.message || 'Gagal membaca CSV');
      }
    };
    reader.readAsText(f, 'UTF-8');
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'template-peta-desa.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const onManualColorRequest = useCallback(({ manualKey, displayName, currentColor }) => {
    scrollBeforeColorModalRef.current =
      window.pageYOffset ?? document.documentElement?.scrollTop ?? document.body?.scrollTop ?? 0;
    setManualEdit({ manualKey, displayName });
    setPickerColor(currentColor || '#1976d2');
  }, []);

  const closeColorModal = useCallback(() => setManualEdit(null), []);

  const restoreScrollAfterModal = useCallback(() => {
    const y = scrollBeforeColorModalRef.current;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, left: 0, behavior: 'auto' });
      });
    });
  }, []);

  const applyManualColor = () => {
    if (!manualEdit) return;
    setSettings((prev) => {
      const next = {
        ...prev,
        polygonManualColors: { ...(prev.polygonManualColors || {}), [manualEdit.manualKey]: pickerColor },
      };
      saveMapSettings(next);
      return next;
    });
    closeColorModal();
  };

  const clearManualColor = () => {
    if (!manualEdit) return;
    setSettings((prev) => {
      const pc = { ...(prev.polygonManualColors || {}) };
      delete pc[manualEdit.manualKey];
      const next = { ...prev, polygonManualColors: pc };
      saveMapSettings(next);
      return next;
    });
    closeColorModal();
  };

  useEffect(() => {
    if (!savedNotice) return undefined;
    const t = setTimeout(() => setSavedNotice(false), 3200);
    return () => clearTimeout(t);
  }, [savedNotice]);

  const tc = Math.max(2, Math.min(8, Number(settings.tierCount) || 4));
  const palette = ensureTierPalette(settings.tierPalette, tc, DEFAULT_MAP_SETTINGS.tierPalette);

  const handleDownloadPdf = useCallback(async () => {
    const ref = mapView === 'desil' ? desilMapRef : bantuanMapRef;
    setPdfLoading(true);
    try {
      await ref.current?.downloadPdf?.();
    } finally {
      setPdfLoading(false);
    }
  }, [mapView]);

  const modalProps = {
    centered: true,
    scrollable: true,
    fade: true,
    className: s.settingsModal,
    contentClassName: s.settingsModalContent,
    backdrop: true,
  };

  return (
    <div className={s.page}>
      <div className={s.mapStage}>
        {savedNotice && (
          <div className={s.toastSuccess} role="status">
            <i className="fa fa-check-circle" aria-hidden="true" />
            <span>Pengaturan disimpan.</span>
            <button type="button" className={s.toastClose} onClick={() => setSavedNotice(false)} aria-label="Tutup">
              ×
            </button>
          </div>
        )}
        {csvError && !settingsPanel && (
          <div className={s.toastError} role="alert">
            <span>{csvError}</span>
            <button type="button" className={s.toastClose} onClick={() => setCsvError('')} aria-label="Tutup">
              ×
            </button>
          </div>
        )}

        <div className={s.floatingToolbar}>
          <div className={s.toolbarTop}>
            <div className={s.viewSwitch} role="tablist" aria-label="Jenis peta">
              <button
                type="button"
                role="tab"
                aria-selected={mapView === 'desil'}
                className={`${s.viewSwitchBtn} ${mapView === 'desil' ? s.viewSwitchBtnActive : ''}`}
                onClick={() => setMapView('desil')}
              >
                DTSEN &amp; kustom
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mapView === 'bantuan'}
                className={`${s.viewSwitchBtn} ${mapView === 'bantuan' ? s.viewSwitchBtnActive : ''}`}
                onClick={() => setMapView('bantuan')}
              >
                Bantuan per desa
              </button>
            </div>
            {mapView === 'bantuan' && (
              <div className={s.bantuanFilters}>
                <Input
                  type="select"
                  bsSize="sm"
                  className={s.filterSelect}
                  value={bantuanTahun}
                  onChange={(e) => setBantuanTahun(e.target.value)}
                  aria-label="Tahun bantuan"
                >
                  {tahunChoices.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Input>
                <Input
                  type="select"
                  bsSize="sm"
                  className={s.filterSelectWide}
                  value={bantuanJenis}
                  onChange={(e) => setBantuanJenis(e.target.value)}
                  aria-label="Jenis bantuan"
                >
                  <option value="all">Semua bantuan</option>
                  {bantuanOptions.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Input>
              </div>
            )}
            <Button
              color="light"
              size="sm"
              className={s.toolBtnPdf}
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              title="Unduh gambar peta + tabel ke PDF (sama seperti di Beranda)"
            >
              <i
                className={`fa ${pdfLoading ? 'fa-spinner fa-spin' : 'fa-file-pdf-o'}`}
                aria-hidden="true"
              />
              <span>{pdfLoading ? 'Memuat…' : 'Unduh PDF'}</span>
            </Button>
          </div>

          <div className={s.toolbarRow}>
            <Button color="primary" size="sm" className={s.toolBtnPrimary} onClick={handleSave}>
              <i className="fa fa-floppy-o mr-1" aria-hidden="true" />
              Simpan
            </Button>
            <span className={s.toolbarDivider} aria-hidden="true" />
            <Button
              color="light"
              size="sm"
              className={s.toolBtn}
              onClick={() => setSettingsPanel('data')}
              title="Sumber data & CSV"
            >
              <i className="fa fa-database" aria-hidden="true" />
              <span>Data</span>
            </Button>
            <Button
              color="light"
              size="sm"
              className={s.toolBtn}
              onClick={() => setSettingsPanel('display')}
              title="Tingkat peta & kecamatan"
            >
              <i className="fa fa-map" aria-hidden="true" />
              <span>Tampilan</span>
            </Button>
            <Button
              color="light"
              size="sm"
              className={s.toolBtn}
              onClick={() => setSettingsPanel('tiers')}
              title="Gradasi & palet"
            >
              <i className="fa fa-barcode" aria-hidden="true" />
              <span>Gradasi</span>
            </Button>
            <Button
              color="light"
              size="sm"
              className={s.toolBtn}
              onClick={() => setSettingsPanel('baseColors')}
              title="Warna dasar peta"
            >
              <i className="fa fa-tint" aria-hidden="true" />
              <span>Warna</span>
            </Button>
            <Button color="link" size="sm" className={s.toolBtnReset} onClick={handleReset}>
              Reset
            </Button>
          </div>
          {mapView === 'desil' ? (
            <p className={s.toolbarHint}>
              Klik wilayah untuk warna manual · Pratinjau langsung; <strong>Simpan</strong> untuk
              localStorage
            </p>
          ) : (
            <p className={s.toolbarHint}>
              Peta distribusi bantuan (data server), sama seperti di Beranda · PDF menyertakan tabel sesuai
              tingkat peta (desa / kecamatan) dan filter kecamatan
            </p>
          )}
        </div>

        {mapView === 'desil' ? (
          <Am4chartMapDesil
            ref={desilMapRef}
            previewSettings={settings}
            previewMode
            customizationEmbed
            onManualColorRequest={onManualColorRequest}
          />
        ) : (
          <Am4chartMap
            ref={bantuanMapRef}
            tahun={bantuanTahun}
            hidePerDesaTable
            hideMapChrome
            customizationEmbed
            mapDomId="mapCustomizationBantuan"
            externalBantuan={{
              value: bantuanJenis,
              onChange: setBantuanJenis,
              options: bantuanOptions,
            }}
            layoutSettings={{
              mapLevel: settings.mapLevel,
              kecamatanIncludedKeys: settings.kecamatanIncludedKeys,
              colors: {
                none: settings.colors?.none,
                stroke: settings.colors?.stroke,
              },
            }}
          />
        )}
      </div>

      <Modal isOpen={settingsPanel === 'data'} toggle={closePanel} size="lg" {...modalProps}>
        <ModalHeader toggle={closePanel} className={s.modalHead}>
          Sumber data
        </ModalHeader>
        <ModalBody className={s.modalBody}>
          {csvError && (
            <div className={s.inlineAlert}>{csvError}</div>
          )}
          <p className={s.hint}>
            Bawaan: <strong>CSV lokal</strong> (awal kosong → wilayah &quot;tidak ada data&quot;). Kolom{' '}
            <code>desa</code>, <code>nilai</code>, opsional <code>kecamatan</code>.
          </p>
          <FormGroup>
            <Label>Sumber</Label>
            <Input
              type="select"
              value={settings.dataSource === 'api' ? 'api' : 'csv'}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, dataSource: e.target.value === 'api' ? 'api' : 'csv' }))
              }
            >
              <option value="csv">CSV (unggah manual)</option>
              <option value="api">API DTSEN (server)</option>
            </Input>
          </FormGroup>
          {settings.dataSource !== 'api' && (
            <>
              <div className={s.btnRow}>
                <Button tag="label" color="primary" size="sm" className="mb-0">
                  Pilih file CSV
                  <input type="file" accept=".csv,text/csv" className="d-none" onChange={onCsvFile} />
                </Button>
                <Button color="secondary" outline size="sm" type="button" onClick={downloadTemplate}>
                  Unduh template
                </Button>
                <Button
                  color="danger"
                  outline
                  size="sm"
                  type="button"
                  onClick={() => {
                    clearMapCsvData();
                    setCsvRowCount(0);
                  }}
                >
                  Hapus CSV
                </Button>
              </div>
              <p className={`${s.hint} ${s.hintTight}`}>Baris terbaca: {csvRowCount}</p>
            </>
          )}
          <p className={`${s.footerNote} ${s.modalFooterNote}`}>
            CSV dan pengaturan disimpan di browser (localStorage). Reset pengaturan tidak menghapus CSV.
          </p>
        </ModalBody>
        <ModalFooter className={s.modalFoot}>
          <Button color="secondary" outline size="sm" onClick={closePanel}>
            Tutup
          </Button>
          <Button color="primary" size="sm" onClick={() => { handleSave(); closePanel(); }}>
            Simpan &amp; tutup
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={settingsPanel === 'display'} toggle={closePanel} size="lg" {...modalProps}>
        <ModalHeader toggle={closePanel} className={s.modalHead}>
          Tampilan wilayah
        </ModalHeader>
        <ModalBody className={s.modalBody}>
          <FormGroup>
            <Label>Tingkat peta</Label>
            <Input
              type="select"
              value={settings.mapLevel === 'kecamatan' ? 'kecamatan' : 'desa'}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  mapLevel: e.target.value === 'kecamatan' ? 'kecamatan' : 'desa',
                  ...(e.target.value !== 'kecamatan' ? { kecamatanIncludedKeys: null } : {}),
                }))
              }
            >
              <option value="desa">Per desa</option>
              <option value="kecamatan">Per kecamatan</option>
            </Input>
          </FormGroup>
          {settings.mapLevel === 'kecamatan' && kecamatanList.length > 0 && (
            <FormGroup className="mt-3 mb-0">
              <Label className="d-block">Kecamatan pada peta</Label>
              <p className={s.hint}>
                Hilangkan centang untuk warna &quot;tidak ada data&quot; dan memfokuskan peta pada yang dipilih.
              </p>
              <FormGroup check className="mb-2">
                <CustomInput
                  innerRef={kecamatanMasterRef}
                  type="checkbox"
                  id="kecamatan-all-select-dlg"
                  label="Semua kecamatan"
                  checked={settings.kecamatanIncludedKeys == null}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSettings((prev) => ({ ...prev, kecamatanIncludedKeys: null }));
                    } else {
                      setSettings((prev) => ({ ...prev, kecamatanIncludedKeys: [] }));
                    }
                  }}
                />
              </FormGroup>
              <div className={s.checkboxList}>
                {kecamatanList.map(({ key, label }) => {
                  const checked =
                    settings.kecamatanIncludedKeys == null ||
                    (settings.kecamatanIncludedKeys && settings.kecamatanIncludedKeys.includes(key));
                  return (
                    <FormGroup check key={key} className="mb-1">
                      <CustomInput
                        type="checkbox"
                        id={`kecamatan-dlg-${key}`}
                        label={label}
                        checked={checked}
                        onChange={() => {
                          setSettings((prev) => {
                            if (prev.kecamatanIncludedKeys == null) {
                              const next = kecamatanList.map((x) => x.key).filter((k) => k !== key);
                              if (next.length === 0) {
                                return { ...prev, kecamatanIncludedKeys: [] };
                              }
                              if (next.length === kecamatanList.length) {
                                return { ...prev, kecamatanIncludedKeys: null };
                              }
                              return { ...prev, kecamatanIncludedKeys: next };
                            }
                            const cur = [...(prev.kecamatanIncludedKeys || [])];
                            const ix = cur.indexOf(key);
                            if (ix >= 0) cur.splice(ix, 1);
                            else cur.push(key);
                            if (cur.length === 0) return { ...prev, kecamatanIncludedKeys: [] };
                            if (cur.length === kecamatanList.length) {
                              return { ...prev, kecamatanIncludedKeys: null };
                            }
                            return { ...prev, kecamatanIncludedKeys: cur };
                          });
                        }}
                      />
                    </FormGroup>
                  );
                })}
              </div>
            </FormGroup>
          )}
          <FormGroup check className="mt-3">
            <CustomInput
              type="checkbox"
              id="showKec-dlg"
              label="Tampilkan nama kecamatan di peta"
              checked={!!settings.showKecamatanLabels}
              onChange={(e) =>
                setSettings((prev) => ({ ...prev, showKecamatanLabels: e.target.checked }))
              }
            />
          </FormGroup>
          <ColorRow fields={labelColorFields} settings={settings} onColorChange={setColor} />
        </ModalBody>
        <ModalFooter className={s.modalFoot}>
          <Button color="secondary" outline size="sm" onClick={closePanel}>
            Tutup
          </Button>
          <Button color="primary" size="sm" onClick={() => { handleSave(); closePanel(); }}>
            Simpan &amp; tutup
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={settingsPanel === 'tiers'} toggle={closePanel} size="lg" {...modalProps}>
        <ModalHeader toggle={closePanel} className={s.modalHead}>
          Gradasi data
        </ModalHeader>
        <ModalBody className={s.modalBody}>
          <p className={s.hintTight} style={{ marginBottom: '0.75rem' }}>
            Warna manual per wilayah: <strong>klik</strong> polygon di peta, lalu atur di dialog warna.
          </p>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Jumlah kelompok warna</Label>
                <Input type="select" value={tc} onChange={(e) => onTierCountChange(e.target.value)}>
                  {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={n}>
                      {n} kelompok
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Mode batas nilai</Label>
                <Input
                  type="select"
                  value={settings.tierMode === 'manual' ? 'manual' : 'quantile'}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      tierMode: e.target.value === 'manual' ? 'manual' : 'quantile',
                    }))
                  }
                >
                  <option value="quantile">Kuantil otomatis</option>
                  <option value="manual">Batas manual</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          {settings.tierMode === 'manual' && (
            <div className="mb-2">
              <Label>Batas atas tiap kelompok ({tc - 1} nilai)</Label>
              {(settings.manualTierBreaks || []).slice(0, tc - 1).map((b, idx) => (
                <FormGroup key={idx} className="mb-2">
                  <Input
                    type="number"
                    step="any"
                    value={b}
                    onChange={(e) => onBreakChange(idx, e.target.value)}
                    placeholder={`Batas ${idx + 1}`}
                  />
                </FormGroup>
              ))}
            </div>
          )}
          <span className={s.paletteLabel}>Palet gradasi</span>
          <Row>
            {palette.map((col, idx) => (
              <Col xs={6} md={4} key={idx} className={s.paletteSwatch}>
                <small className="d-block text-muted mb-1">Kelompok {idx + 1}</small>
                <Input
                  type="color"
                  value={col}
                  onChange={(e) => onPaletteChange(idx, e.target.value)}
                  style={{ width: '100%', height: 36, cursor: 'pointer' }}
                />
              </Col>
            ))}
          </Row>
        </ModalBody>
        <ModalFooter className={s.modalFoot}>
          <Button color="secondary" outline size="sm" onClick={closePanel}>
            Tutup
          </Button>
          <Button color="primary" size="sm" onClick={() => { handleSave(); closePanel(); }}>
            Simpan &amp; tutup
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={settingsPanel === 'baseColors'} toggle={closePanel} size="md" {...modalProps}>
        <ModalHeader toggle={closePanel} className={s.modalHead}>
          Warna dasar peta
        </ModalHeader>
        <ModalBody className={s.modalBody}>
          <p className={s.hint}>
            Warna untuk wilayah <strong>tanpa data</strong> atau di bawah kelompok terendah.
          </p>
          <ColorRow
            fields={[{ key: 'none', label: 'Tidak ada data' }]}
            settings={settings}
            onColorChange={setColor}
          />
          <ColorRow fields={edgeFields} settings={settings} onColorChange={setColor} />
        </ModalBody>
        <ModalFooter className={s.modalFoot}>
          <Button color="secondary" outline size="sm" onClick={closePanel}>
            Tutup
          </Button>
          <Button color="primary" size="sm" onClick={() => { handleSave(); closePanel(); }}>
            Simpan &amp; tutup
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!manualEdit}
        toggle={closeColorModal}
        autoFocus={false}
        returnFocusAfterClose={false}
        onClosed={restoreScrollAfterModal}
        centered
        className={s.settingsModal}
        contentClassName={s.settingsModalContent}
      >
        <ModalHeader toggle={closeColorModal}>Warna wilayah</ModalHeader>
        <ModalBody>
          {manualEdit && (
            <>
              <p className="mb-2">
                <strong>{manualEdit.displayName || manualEdit.manualKey}</strong>
              </p>
              <p className="small text-muted mb-3">{manualEdit.manualKey}</p>
              <FormGroup>
                <Label>Pilih warna</Label>
                <Input
                  type="color"
                  value={pickerColor}
                  onChange={(e) => setPickerColor(e.target.value)}
                  style={{ width: 80, height: 40, cursor: 'pointer' }}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="link" className="text-danger" onClick={clearManualColor}>
            Hapus warna manual
          </Button>
          <Button color="secondary" outline onClick={closeColorModal}>
            Batal
          </Button>
          <Button color="primary" onClick={applyManualColor}>
            Terapkan
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default MapCustomizationPage;
